// Phase 5d — the REAL curve-image provider for generateReport's
// registerCurveImageProvider seam (registered by components/FanDataTab.tsx at
// module scope, so it is in place before any dispatch the page can fire).
//
// Two legacy flows produce `img[]` payloads:
//
//  1. fan_data multi-AHU (fan_data.js:175-225): the batched dispatch fetches
//     each AHU's fan rows SEQUENTIALLY (POST fan_selection {ahu_id,
//     fan_data:true}), renders them into the tab (each render replaces the
//     last — reproduced as a reportTabData cache write the FanDataTab pane
//     subscribes to), rasterizes every fan canvas, and returns
//     `${ahuId}-${fanId}_${dataURL}` entries. The per-AHU fetch is part of
//     THIS provider (it is the render/capture source, not a separable probe);
//     a fetch failure REJECTS, which kills the batched ZIP POST exactly like
//     the legacy promise chain (:191-207 — a rejected $.ajax skips every
//     later .then, final POST included).
//
//  2. submittal_package preGenerateReport (submittal.package.js:395-417):
//     GET the project's fans (/api/v1.0/ahu/fans/?project_id=…,
//     collection/fans.collection.js), draw the submittal curve variant on
//     detached canvases, return `${fanId}_${dataURL}` for the DRAWN fans only
//     (ctxArray, :171-180 — guard-failing fans stay uncaptured). A fetch
//     failure rejects → the kit's submittal dispatch never POSTs, matching
//     the legacy `fetch().then(…)` that simply never continues; a draw throw
//     (unconditional JSON.parse(bhp), :144) aborts the same way.

import { postToCelery } from 'app/celeryRoundTrip';
import { legacyTimestamp, type GenerateState } from './generateReport';
import {
  fetchCollection,
  generateStateKey,
  storeGeneratedReport,
} from './reportTabData';
import {
  captureFanCanvases,
  createFanCanvases,
  drawFanDataCurves,
  drawSubmittalFanCurve,
  hasDrawableCurve,
  prepareFanRows,
  type FanCanvasEntry,
  type FanDataRow,
} from './fanCurves';

/** The provider registered with registerCurveImageProvider — branches on the
 *  dispatching report (the seam's only two callers). */
export async function collectReportCurveImages(state: GenerateState): Promise<string[]> {
  if (state.current_report === 'submittal_package') {
    return submittalCurveImages(state);
  }
  return fanDataMultiCurveImages(state);
}

/** fan_data.js:180-205 — the sequential per-AHU fetch → render → capture. */
async function fanDataMultiCurveImages(state: GenerateState): Promise<string[]> {
  const allImages: string[] = [];
  for (const ahuId of state.ahu_ids ?? []) {
    // :180-189 — rejection propagates (chain dies, batch POST skipped).
    // eslint-disable-next-line no-await-in-loop
    const fanData = await postToCelery<FanDataRow[]>('/reports/fan_selection', {
      ahu_id: String(ahuId),
      fan_data: true,
    });
    if (!fanData || !fanData.length) continue; // :196
    // :197 — renderReport errors are warn-and-continue; canvases that made it
    // past the html() insert are still captured below (a get_si_params parse
    // throw happens BEFORE the insert, so those fans yield nothing).
    let entries: FanCanvasEntry[] = [];
    try {
      prepareFanRows(fanData);
      entries = createFanCanvases(fanData);
      // the html() insert moment — the pane re-renders this AHU's fans
      // (successive writes reproduce the render-replaces-render sequence).
      storeGeneratedReport(generateStateKey(state), {
        models: fanData as Array<Record<string, unknown>>,
        timeStamp: legacyTimestamp(),
      });
      drawFanDataCurves(entries, fanData);
    } catch {
      // console.warn('renderReport error for AHU', ahuId) equivalent — the
      // canvases created before the throw still exist, like the legacy DOM.
    }
    allImages.push(...captureFanCanvases(entries, (id) => `${String(ahuId)}-${id}`)); // :198-203
  }
  return allImages;
}

/** submittal.package.js preGenerateReport → drawCanvas → drawCurves →
 *  getCanvasData (:395-417, :130-180, :386-392). */
async function submittalCurveImages(state: GenerateState): Promise<string[]> {
  // fans.collection.js — the whole project's fans.
  const fans = await fetchCollection<FanDataRow[]>(
    `/api/v1.0/ahu/fans/?project_id=${state.project_id}`
  );
  const rows = Array.isArray(fans) ? fans : [];
  // :410-416 — one detached canvas per fan (curve or not)…
  const entries = createFanCanvases(rows);
  const byId = new Map(entries.map((e) => [e.id, e.canvas]));
  // …but only DRAWN canvases reach ctxArray/getCanvasData (:171-180). A draw
  // throw propagates: the legacy _.each aborts and generateReport never runs.
  const drawn: FanCanvasEntry[] = [];
  rows.forEach((model) => {
    if (hasDrawableCurve(model)) {
      const canvas = byId.get(model.id);
      if (canvas) {
        drawSubmittalFanCurve(canvas, model);
        drawn.push({ id: model.id, canvas });
      }
    }
  });
  // :386-392 — `${canvas.id}_${dataURL}` (canvas.id was set to the fan id).
  return captureFanCanvases(drawn, (id) => String(id));
}
